import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useIsAppAdmin, useWaysteClient } from '@alliance-disposal/client';
import { Hauler, Profile } from '@alliance-disposal/transport-types';
import { useAuth0AuthGate } from '@wayste/auth-ui-shell';
import { AppDataProvider, Loading } from '@wayste/sour-components';
import { SourProvider } from '@wayste/sour-context';
import { SourSearchAppProvider, SourSearchProviderOld } from '@wayste/sour-search';
import { datadogRum } from '@datadog/browser-rum';
import posthog from 'posthog-js';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { S3Context } from './context';
import { useOnboardingSteps } from './pages/welcome/use-onboarding-steps';
import InactiveAccountView from './structure/InactiveAccountView';
import InactiveUserView from './structure/InactiveUserView';
import Layout from './structure/Layout';
import { routes } from './utils';
import { buildWaysteAppDataAdapter } from './utils/app-data-adapter';
import { identifyUserflowUser, resetUserflow } from './utils/userflow';

const App = () => {
    const { isResolving: isAuthResolving } = useAuth0AuthGate();
    const client = useWaysteClient();
    const { uploadOrderImage, deleteS3FileFromFolder } = useContext(S3Context);
    // Built after authentication/authorization resolve (see the authenticated branch below) so the
    // shared roll-off components reach the adapter wherever they render under Layout — the create
    // orchestrator, the schedule-switch drawer, and the standalone customer/contact surfaces alike.
    // Memoized: adapter identity must be stable (hooks list its members in dependency arrays).
    const appDataAdapter = useMemo(
        () => buildWaysteAppDataAdapter(client, { uploadOrderImage, deleteS3FileFromFolder }),
        [client, uploadOrderImage, deleteS3FileFromFolder],
    );
    const navigate = useNavigate();
    const location = useLocation();
    const { isAdmin } = useIsAppAdmin();
    const [user, setUser] = useState<Profile.ProfileTransport | null>(null);
    const [hauler, setHauler] = useState<Hauler.HaulerTransport | null>(null);
    const [isDataLoading, setIsDataLoading] = useState<boolean>(false);
    const [isAuthLoading, setIsAuthLoading] = useState<boolean>(true);
    const [adminDismissedOnboarding, setAdminDismissedOnboarding] = useState(false);
    const authLoadedBefore = useRef(false);
    const { steps: onboardingSteps, isLoading: isOnboardingResolving } = useOnboardingSteps(user, hauler);

    const handleInitialSetupAfterAuth = () => {
        client.equipment().wayste.containerType.setEquipmentListInMemory();
    };

    useEffect(() => {
        const authLoading = client.auth().loading.subscribe((loading) => {
            if (authLoadedBefore.current) return; // Skip if auth has loaded before
            if (!loading) {
                authLoadedBefore.current = true;
            }
            setIsAuthLoading(loading);
        });

        const authSubscription = client.auth().currentAuthority.subscribe(async (auth) => {
            if (auth != null) {
                setIsDataLoading(true);
                datadogRum.setUser({ id: auth.profileID });
                posthog.identify(auth.profileID);
                try {
                    const [haulerData, userData] = await Promise.all([client.hauler().wayste.fetch(), client.profile().fetchProfile()]);
                    setHauler(haulerData);
                    setUser(userData);
                    posthog.identify(auth.profileID, {
                        email: userData.email,
                        haulerId: haulerData.id,
                        partnerPortal: haulerData.waysteLite,
                        haulerName: haulerData.name,
                    });
                    identifyUserflowUser(userData, haulerData);
                    posthog.group('hauler', haulerData.id);
                    handleInitialSetupAfterAuth();
                } catch (error) {
                    console.error('Error fetching hauler data:', error);
                } finally {
                    setIsDataLoading(false);
                }
            } else {
                setUser(null);
                setHauler(null);
                posthog.reset();
                resetUserflow();
            }
        });

        // The profile is otherwise read once per sign-in, which leaves this stale the moment the user
        // edits it — the setup screen below gates on their name, so it would send them straight back.
        const profileSubscription = client.profile().currentUserProfileObservable.subscribe((profile) => profile && setUser(profile));

        // Same for the hauler: onboarding writes materials and sizes onto it, and the gate below reads
        // them, so finishing setup has to be visible here or the user is redirected back into it.
        const haulerSubscription = client.hauler().wayste.haulerObservable.subscribe((account) => account && setHauler(account));

        return () => {
            authLoading.unsubscribe();
            authSubscription.unsubscribe();
            profileSubscription.unsubscribe();
            haulerSubscription.unsubscribe();
        };
    }, []);

    const renderContent = () => {
        if (isAuthLoading || isDataLoading || isAuthResolving) {
            return <Loading />;
        }

        // If the company (hauler) is inactive, show the inactive account view.
        if (user && hauler) {
            if (hauler.active === false) {
                return <InactiveAccountView onSignOut={() => client.auth().signOut()} />;
            }

            if (user.active === false) {
                return <InactiveUserView onSignOut={() => client.auth().signOut()} />;
            }

            // Deciding before the step list settles would flash Layout at a user who still owes setup.
            if (isOnboardingResolving) {
                return <Loading />;
            }

            // Onboarding renders outside Layout, so no nav chrome appears until it is finished.
            if (onboardingSteps.length > 0 && !adminDismissedOnboarding) {
                if (location.pathname !== routes.public.welcome) {
                    return <Navigate to={routes.public.welcome} replace />;
                }

                return (
                    <Outlet
                        context={{
                            userProfile: user,
                            waysteLite: hauler.waysteLite,
                            isAppAdmin: isAdmin,
                            onExitSetup: isAdmin ? () => setAdminDismissedOnboarding(true) : undefined,
                        }}
                    />
                );
            }

            if (location.pathname === routes.public.welcome) {
                return <Navigate to={routes.dashboard} replace />;
            }

            return (
                <AppDataProvider adapter={appDataAdapter}>
                    <SourSearchAppProvider
                        environmentOptions={{
                            searchApiBaseUrl: import.meta.env.VITE_ELASTIC_BASE_URL,
                            application: 'wayste',
                            apiKey: import.meta.env.VITE_ELASTIC_KEY as string,
                            environment: import.meta.env.VITE_ELASTIC_ENVIRONMENT,
                        }}
                        globalRestrictTo={hauler.id}
                    >
                        <SourSearchProviderOld
                            onSeeAllClick={(q) => {
                                navigate(routes.search.list + `?query[query]=${q}&sort[direction]=DESC&sour-search=true`);
                            }}
                        >
                            <Layout userProfile={user} isAdmin={isAdmin} onSignOut={() => client.auth().signOut()} />
                        </SourSearchProviderOld>
                    </SourSearchAppProvider>
                </AppDataProvider>
            );
        }

        return <Outlet context={{ userProfile: user, waysteLite: hauler?.waysteLite ?? false, isAppAdmin: false }} />;
    };

    // Wraps every branch, not just the authenticated one, so public routes can reach the alert,
    // toast and confirmation surfaces instead of silently hitting the no-op default context.
    return (
        <SourProvider application="wayste" isAppAdmin={isAdmin}>
            {renderContent()}
        </SourProvider>
    );
};

export default App;
